import React, {
  useState, useEffect, useRef, useCallback, useMemo, forwardRef, useImperativeHandle,
} from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useSidebar } from '../../contexts/SidebarContext';
import SectionPanel from './SectionPanel';
import SkillCard from './SkillCard';
import SkillDetailModal from './SkillDetailModal';
import SkillAdminModal from './SkillAdminModal';
import SaveSkillDialog from './SaveSkillDialog';
import FilterPills from '../ui/FilterPills';
import { Sparkles, Lock, Plus, ArrowRight, Wand2, Palette, UserRound } from 'lucide-react';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Skills do SISTEMA em painéis por `tipo` (livre, criado pelo admin; ex.:
 * "Skill Principal" em cima, "Estilo Visual" embaixo). Painéis no padrão do
 * Text to Speech: dp-panel + barra de gradiente + header interno com badge.
 * A ordem das seções segue a menor `ordem` entre as skills de cada tipo.
 *
 * Expõe openNewSkill() via ref (botão "Nova Skill" fica no PageHeader).
 *
 * Gate premium: o estado bloqueado é responsabilidade DESTE componente
 * (o interceptor global engole o 403 PREMIUM_REQUIRED em silêncio);
 * a API é a barreira real: free não recebe nem metadados.
 */
const SkillsLibrarySection = forwardRef(({ isAdmin, canais = [], onUserSkillsChanged }, ref) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useSidebar();

  const [hasAccess, setHasAccess] = useState(null); // null = carregando
  const [skills, setSkills] = useState([]);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [viewingSkill, setViewingSkill] = useState(null);
  const [savingSkill, setSavingSkill] = useState(null); // skill sendo salva nas minhas
  const [adminModal, setAdminModal] = useState({ open: false, editing: null, presetTipo: null });
  const [filterPlataforma, setFilterPlataforma] = useState('all');
  const detailCache = useRef({}); // { [skillId]: {conteudo, instrucoes, video_url} }

  useImperativeHandle(ref, () => ({
    openNewSkill: () => setAdminModal({ open: true, editing: null, presetTipo: null }),
  }));

  // ── Acesso premium (flag do /auth/subscription) ──
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/auth/subscription`)
      .then(({ data }) => {
        if (alive) setHasAccess(!!data.is_super_admin || !!data.skills_library_acesso);
      })
      .catch(() => { if (alive) setHasAccess(false); });
    return () => { alive = false; };
  }, []);

  const fetchSkills = useCallback(async () => {
    setLoadingSkills(true);
    try {
      const { data } = await axios.get(`${API}/skills`);
      setSkills(data.skills || []);
    } catch (e) {
      setSkills([]);
    } finally {
      setLoadingSkills(false);
    }
  }, []);

  useEffect(() => {
    if (hasAccess) fetchSkills();
  }, [hasAccess, fetchSkills]);

  // ── Detalhe sob demanda com cache (a lista não traz conteudo/instrucoes) ──
  const getDetail = useCallback(async (skill) => {
    if (detailCache.current[skill.id]) return detailCache.current[skill.id];
    const { data } = await axios.get(`${API}/skills/${skill.id}`);
    const detail = {
      conteudo: data.conteudo || '',
      instrucoes: data.instrucoes || '',
      video_url: data.video_url || '',
    };
    detailCache.current[skill.id] = detail;
    return detail;
  }, []);

  // Contador de cópias: fire-and-forget + bump otimista no estado local.
  const trackCopy = useCallback((skillId) => {
    axios.post(`${API}/skills/${skillId}/track-copy`).catch(() => {});
    setSkills((prev) => prev.map((s) => (
      s.id === skillId ? { ...s, copies_count: (s.copies_count || 0) + 1 } : s
    )));
  }, []);

  const handleCopySkill = useCallback(async (skill) => {
    try {
      const detail = await getDetail(skill);
      await navigator.clipboard.writeText(detail.conteudo);
      toast.success(t('skills.detail.copied'));
      trackCopy(skill.id);
    } catch (e) {
      toast.error(t('skills.detail.error_copy'));
      throw e;
    }
  }, [getDetail, t, trackCopy]);

  // ── Admin ──
  const handleDeleteSkill = useCallback(async (skill) => {
    if (!window.confirm(t('skills.admin.delete_confirm'))) return;
    try {
      await axios.delete(`${API}/skills/${skill.id}`);
      toast.success(t('skills.admin.deleted'));
      delete detailCache.current[skill.id];
      fetchSkills();
    } catch (e) {
      toast.error(t('skills.admin.error_save'));
    }
  }, [t, fetchSkills]);

  const handleSaved = useCallback(() => {
    detailCache.current = {}; // conteúdo pode ter mudado
    fetchSkills();
  }, [fetchSkills]);

  // ── Filtro por plataforma (Veo Flow, Midjourney, ...) ──
  const plataformasDisponiveis = useMemo(() => {
    const set = new Set();
    for (const s of skills) for (const p of (s.plataformas || [])) set.add(p);
    return [...set].sort();
  }, [skills]);

  const visibleSkills = useMemo(
    () => (filterPlataforma === 'all'
      ? skills
      : skills.filter((s) => (s.plataformas || []).includes(filterPlataforma))),
    [skills, filterPlataforma]
  );

  // ── Seções por tipo (ordem = menor `ordem` dentro do tipo) ──
  const sections = useMemo(() => {
    const map = new Map();
    for (const s of visibleSkills) {
      const key = s.tipo || '';
      if (!map.has(key)) map.set(key, { tipo: key, skills: [], minOrdem: Infinity });
      const g = map.get(key);
      g.skills.push(s);
      g.minOrdem = Math.min(g.minOrdem, Number.isFinite(s.ordem) ? s.ordem : 0);
    }
    return [...map.values()].sort((a, b) => a.minOrdem - b.minOrdem);
  }, [visibleSkills]);

  const existingTipos = useMemo(
    () => [...new Set(skills.map((s) => s.tipo).filter(Boolean))],
    [skills]
  );

  // Biblioteca com muitas skills: cards menores, mais colunas por linha.
  const gridStyle = {
    display: 'grid', gap: '12px',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(205px, 1fr))',
  };

  const skeletonGrid = (
    <div style={gridStyle}>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="dp-skeleton" style={{ height: '150px', borderRadius: '12px' }} />
      ))}
    </div>
  );

  return (
    <div>
      {hasAccess === null || (hasAccess && loadingSkills && skills.length === 0) ? (
        <SectionPanel icon={Sparkles} title={t('skills.system_title')}>
          {skeletonGrid}
        </SectionPanel>
      ) : hasAccess === false ? (
        /* ── Estado bloqueado (só plano free): vitrine sem dados reais ── */
        <SectionPanel icon={Sparkles} title={t('skills.system_title')} actions={
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            fontSize: '11px', fontWeight: 600, color: 'var(--dp-blue-500)',
            fontFamily: 'var(--dp-font)',
            background: 'rgba(13, 174, 255, 0.08)', border: '1px solid rgba(13, 174, 255, 0.22)',
            padding: '3px 10px', borderRadius: '9999px',
          }}>
            <Lock size={10} />
            {t('skills.locked_badge')}
          </span>
        }>
          <div style={{ position: 'relative' }}>
            <div style={{
              display: 'grid', gap: '14px',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              filter: 'blur(3px)', opacity: 0.3, pointerEvents: 'none', userSelect: 'none',
            }}>
              {[Wand2, Palette, UserRound].map((Icon, i) => (
                <div key={i} style={{
                  aspectRatio: '16 / 9', borderRadius: '12px',
                  background: 'linear-gradient(135deg, var(--dp-bg-elevated), var(--dp-bg-card))',
                  border: '1px solid var(--dp-border-medium)',
                  display: (isMobile && i > 0) ? 'none' : 'flex',
                  alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={28} style={{ color: 'rgba(13, 174, 255, 0.45)' }} />
                </div>
              ))}
            </div>
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: '10px',
              textAlign: 'center', padding: '20px',
            }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: '46px', height: '46px', borderRadius: '50%',
                background: 'rgba(13, 174, 255, 0.10)', border: '1px solid rgba(13, 174, 255, 0.25)',
              }}>
                <Lock size={20} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
              </span>
              <h3 style={{
                margin: 0, fontSize: '15.5px', fontWeight: 700,
                color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)',
              }}>
                {t('skills.locked_title')}
              </h3>
              <p style={{
                margin: 0, maxWidth: '440px', fontSize: '12.5px', lineHeight: 1.55,
                color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)',
              }}>
                {t('skills.locked_message')}
              </p>
              <button
                type="button"
                className="dp-btn-cta dp-btn-cta--sm"
                onClick={() => navigate('/meu-plano')}
                style={{ marginTop: '4px' }}
              >
                <Sparkles size={14} />
                {t('skills.locked_cta')}
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </SectionPanel>
      ) : (
        <>
          {/* Filtro por plataforma (aparece com 2+ plataformas no acervo) */}
          {plataformasDisponiveis.length >= 2 && (
            <div style={{ marginBottom: '14px' }}>
              <FilterPills
                options={[
                  { value: 'all', label: t('skills.platforms.all') },
                  ...plataformasDisponiveis.map((p) => ({ value: p, label: p })),
                ]}
                value={filterPlataforma}
                onChange={setFilterPlataforma}
              />
            </div>
          )}
          {sections.length === 0 ? (
            <SectionPanel icon={Sparkles} title={t('skills.system_title')}>
              <div style={{
                padding: '24px 20px', textAlign: 'center',
                color: 'var(--dp-text-tertiary)', fontSize: '12.5px',
                fontFamily: 'var(--dp-font)', lineHeight: 1.5,
              }}>
                {skills.length === 0 ? t('skills.empty') : t('skills.my.empty_filtered')}
              </div>
            </SectionPanel>
          ) : (
        /* ── Um painel por tipo ── */
        sections.map((section) => (
          <SectionPanel
            key={section.tipo}
            icon={Sparkles}
            title={section.tipo}
            count={section.skills.length}
            collapsible
            storageKey={`system:${section.tipo}`}
            actions={isAdmin ? (
              <button
                type="button"
                title={t('skills.admin.new_skill')}
                onClick={() => setAdminModal({ open: true, editing: null, presetTipo: section.tipo })}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: '26px', height: '26px', borderRadius: '7px',
                  border: '1px solid var(--dp-border-subtle)', background: 'transparent',
                  color: 'var(--dp-text-secondary)', cursor: 'pointer', transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dp-blue-500)'; e.currentTarget.style.borderColor = 'rgba(13, 174, 255, 0.35)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dp-text-secondary)'; e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
              >
                <Plus size={14} />
              </button>
            ) : null}
          >
            <div style={gridStyle}>
              {section.skills.map((skill) => (
                <SkillCard
                  key={skill.id}
                  skill={skill}
                  variant="system"
                  onOpen={setViewingSkill}
                  onCopy={handleCopySkill}
                  onSave={setSavingSkill}
                  isAdmin={isAdmin}
                  onEdit={(s) => setAdminModal({ open: true, editing: s, presetTipo: null })}
                  onDelete={handleDeleteSkill}
                />
              ))}
            </div>
          </SectionPanel>
        ))
          )}
        </>
      )}

      {/* ── Modais ── */}
      <SkillDetailModal
        skill={viewingSkill}
        variant="system"
        onClose={() => setViewingSkill(null)}
        getDetail={getDetail}
        onSave={setSavingSkill}
        onCopied={(s) => trackCopy(s.id)}
        isMobile={isMobile}
      />
      {savingSkill && (
        <SaveSkillDialog
          skill={savingSkill}
          canais={canais}
          getDetail={getDetail}
          onClose={() => setSavingSkill(null)}
          onSaved={onUserSkillsChanged}
        />
      )}
      {adminModal.open && (
        <SkillAdminModal
          editing={adminModal.editing}
          presetTipo={adminModal.presetTipo}
          existingTipos={existingTipos}
          onClose={() => setAdminModal({ open: false, editing: null, presetTipo: null })}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
});

SkillsLibrarySection.displayName = 'SkillsLibrarySection';

export default SkillsLibrarySection;
